/**
 * Tabbed activity region consolidating item history, VAS jobs (repair /
 * cleaning / photography), and escalations into one section with counts.
 * Tabs with no rows are hidden; the section disappears when empty.
 *
 * Each table is a react-admin <Datagrid> fed by useList()/ListContextProvider
 * rather than hand-built MUI tables — the framework renders the rows, sorting,
 * and empty states, with plain <TextField>/<DateField> columns plus a
 * <FunctionField> for derived cells like the details column.
 */
import { useState } from 'react';
import {
  Datagrid,
  DateField,
  FunctionField,
  ListContextProvider,
  TextField,
  useList,
} from 'react-admin';
import { Box, Tab, Tabs } from '@mui/material';

import { SectionLabel } from './ItemDetailSections';
import type { InventoryItemRecord, ItemJob, RepairJob, StateTransition, Escalation } from './types';

/** Ensure every embedded row has an `id` so useList can key/sort it. */
const withIds = <T extends { id?: string | number, uuid?: string }>(rows: T[]) =>
  rows.map((row, index) => ({ ...row, id: row.id ?? row.uuid ?? index }));

/** Small wrapper: turns a local array into a ListContext for a <Datagrid>. */
const ActivityGrid = ({ rows, children }: { rows: object[], children: React.ReactNode }) => {
  const listContext = useList({ data: withIds(rows as Array<{ id?: string | number, uuid?: string }>) });
  return (
    <ListContextProvider value={listContext}>
      <Datagrid bulkActionButtons={false} rowClick={false} sx={{ '& .RaDatagrid-rowCell': { whiteSpace: 'nowrap' } }}>
        {children}
      </Datagrid>
    </ListContextProvider>
  );
};

const HistoryTable = ({ rows }: { rows: StateTransition[] }) => (
  <ActivityGrid rows={rows}>
    <DateField source="createdAt" label="Timestamp" showTime emptyText="—" />
    <TextField source="changeType" label="Type" emptyText="—" />
    <TextField source="changeFrom" label="From" emptyText="—" />
    <TextField source="changeTo" label="To" emptyText="—" />
    <TextField source="reason" label="Reason" emptyText="—" />
    <TextField source="note" label="Notes" emptyText="—" />
    <FunctionField
      label="Details"
      render={(t: StateTransition) => (t.changeDetails ? JSON.stringify(t.changeDetails) : '—')}
      sx={{ maxWidth: 280, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis' }}
    />
    <TextField source="user.name" label="User" emptyText="—" />
  </ActivityGrid>
);

const JobsTable = ({ rows }: { rows: Array<ItemJob | RepairJob> }) => (
  <ActivityGrid rows={rows}>
    <TextField source="status" label="Status" emptyText="—" />
    <DateField source="createdAt" label="Created" showTime emptyText="—" />
    <DateField source="startedAt" label="Started" showTime emptyText="—" />
    <DateField source="declinedAt" label="Declined" showTime emptyText="—" />
    <DateField source="completedAt" label="Completed" showTime emptyText="—" />
    <FunctionField
      label="Details"
      render={(job: ItemJob | RepairJob) =>
        ('repairJobDetails' in job && job.repairJobDetails?.length ?
          job.repairJobDetails.map(d => `${d.type ?? ''}${d.location ? ` @ ${d.location}` : ''}`).join(', ') :
          '—')}
    />
  </ActivityGrid>
);

const EscalationsTable = ({ rows }: { rows: Escalation[] }) => (
  <ActivityGrid rows={rows}>
    <DateField source="createdAt" label="Created" showTime emptyText="—" />
    <TextField source="triggerTypeDisplay" label="Trigger" emptyText="—" />
    <TextField source="shipmentTypeDisplay" label="Shipment Type" emptyText="—" />
    <TextField source="reasonDisplay" label="Reason" emptyText="—" />
    <TextField source="closedReason" label="Closed Reason" emptyText="—" />
    <DateField source="closedAt" label="Closed At" showTime emptyText="—" />
  </ActivityGrid>
);

export const ItemActivityTabs = ({ record }: { record: InventoryItemRecord }) => {
  const tabs = [
    { key: 'history', label: 'History', count: record.stateTransitions?.length ?? 0, render: () => <HistoryTable rows={record.stateTransitions ?? []} /> },
    { key: 'repair', label: 'Repair Jobs', count: record.repairJobs?.length ?? 0, render: () => <JobsTable rows={record.repairJobs ?? []} /> },
    { key: 'cleaning', label: 'Cleaning Jobs', count: record.cleaningJobs?.length ?? 0, render: () => <JobsTable rows={record.cleaningJobs ?? []} /> },
    { key: 'photography', label: 'Photography Jobs', count: record.photographyJobs?.length ?? 0, render: () => <JobsTable rows={record.photographyJobs ?? []} /> },
    { key: 'escalations', label: 'Escalations', count: record.escalations?.length ?? 0, render: () => <EscalationsTable rows={record.escalations ?? []} /> },
  ].filter(tab => tab.count > 0);

  const [active, setActive] = useState(0);
  if (!tabs.length) return null;
  const activeTab = tabs[Math.min(active, tabs.length - 1)];

  return (
    <Box>
      <SectionLabel>Activity</SectionLabel>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
        <Tabs
          value={Math.min(active, tabs.length - 1)}
          onChange={(_event, value: number) => setActive(value)}
          sx={{
            minHeight: 36,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 36, fontSize: 12.5, px: 1.75 },
          }}
        >
          {tabs.map(tab => (
            <Tab key={tab.key} label={`${tab.label} (${tab.count})`} />
          ))}
        </Tabs>
        <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
          {activeTab.render()}
        </Box>
      </Box>
    </Box>
  );
};

export default ItemActivityTabs;
